import yaml from 'js-yaml';
import { useCallback, useEffect, useRef, useState } from 'react';
import { makeStyles } from '@bymason-private/controller-v1-theme-provider';
import { ArtifactModel, ConfigModel, useCreateArtifact } from '@bymason/controller-api-hooks';
import {
  Button,
  GridRowParams,
  GridSelectionModel,
  ControllerGridQuickFilterToolbar,
  CircularProgress,
  Modal,
  Box,
} from '@bymason-private/legacy-pattern-components';
import { DropzoneContainer, DropzoneDropEvent } from '../../../../../common/dropzone-container';
import ArtifactsList from '../../../../artifacts/artifacts-list';
import { DropzoneRef } from 'react-dropzone';
import { useAuth } from '@bymason-private/auth-provider';
import { ARTIFACT_SELECTION_DEFAULTS, ErrorReason, getErrorMessageFromReason, SelectionType } from './logic';
import { getDataSize } from '@bymason/utils';

const useStyles = makeStyles(() => ({
  sectionHead: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    columnGap: 8,
    '& :first-child': {
      maxWidth: 700,
    },
  },
  gridContainer: {
    minHeight: 200,
    margin: '12px 0',
  },
  uploadButton: {
    minWidth: 120,
    maxWidth: 150,
  },
}));

export default function SelectArtifact({
  artifacts,
  artifactsBusy,
  selectionType,
  singleSelect,
  selected,
  header,
  showDropzone,
  disableUpload,
  isRowSelectable,
  onSelect,
  onLoadConfig,
  onUploadSuccess,
  onError,
  children,
}: SelectArtifactProps) {
  const classes = useStyles();
  const { authToken = '' } = useAuth();
  const [uploadArtifact, uploadResult, uploadError, uploadBusy, , , clearUpload] = useCreateArtifact(
    authToken
  );
  const [uploadedConfig, setUploadedConfig] = useState<ConfigModel>();
  const [fileName, setFileName] = useState('');
  const [fileSize, setFileSize] = useState<number>();

  const dropzoneRef = useRef<DropzoneRef>();

  const resetUpload = useCallback(() => {
    clearUpload();
    setFileName('');
    setFileSize(undefined);
  }, [clearUpload]);

  const handleSingleSelect = useCallback(
    (rowIds: GridSelectionModel) => {
      // when `singleSelect=true`, select only the artifact with the last rowId
      const singleSelected = rowIds.filter((item, idx, arr) => idx === arr.length - 1);
      onSelect && onSelect(artifacts?.filter((art) => singleSelected.includes(art.composite)) || []);
    },
    [artifacts, onSelect]
  );

  const handleMultiSelect = useCallback(
    (rowIds: GridSelectionModel) => {
      onSelect && onSelect(artifacts?.filter((art) => rowIds.includes(art.composite)) || []);
    },
    [artifacts, onSelect]
  );

  const handleUploadFileButtonClick = () => {
    if (dropzoneRef.current) {
      dropzoneRef.current.open();
    }
  };

  const handleDrop: DropzoneDropEvent = useCallback(
    (dropResult) => {
      const { files, rejectionCount } = dropResult;
      resetUpload();

      if (files.length > 1 || rejectionCount > 1) {
        onError && onError(getErrorMessageFromReason('over-max-upload'), 'over-max-upload');
      } else if (files.length === 1) {
        const file = files[0];

        /**
         * if this is a config file being uploaded
         * pass the json to onLoadConfig
         */
        if (selectionType === 'config') {
          files[0]
            .text()
            .then((content) => {
              try {
                const loadedConfig = yaml.load(content) as ConfigModel;
                setUploadedConfig(loadedConfig);
              } catch (err) {
                onError && onError(getErrorMessageFromReason('config-parse-error'), 'config-parse-error');
              }
            })
            .catch((err) => {
              onError &&
                onError(
                  `${getErrorMessageFromReason('config-load-error')}: ${(err as Error).message}`,
                  'config-load-error'
                );
            });

          return;
        } else {
          /**
           * if this is NOT a config file being uploaded
           * call the upload artifact api
           */
          setFileName(file.name);
          setFileSize(file.size);
          uploadArtifact({
            type: selectionType,
            file,
            fileName: file.name,
          });
        }
      } else {
        rejectionCount && onError && onError(getErrorMessageFromReason('file-rejected'), 'file-rejected');
      }
    },
    [selectionType, uploadArtifact, resetUpload, onError]
  );

  useEffect(() => {
    if (uploadError) {
      onError &&
        onError(
          `${getErrorMessageFromReason('apk-media-upload-error')}: ${uploadError}`,
          'apk-media-upload-error'
        );
    }
  }, [uploadError, onError]);

  useEffect(() => {
    if (uploadedConfig) {
      onLoadConfig && onLoadConfig(uploadedConfig);
    }
    if (uploadResult && !uploadBusy) {
      onUploadSuccess && onUploadSuccess(uploadResult);
    }
  }, [onLoadConfig, onUploadSuccess, uploadResult, uploadBusy, uploadedConfig]);

  return (
    <section>
      <div className={classes.sectionHead}>
        <div>{header}</div>
        <Button
          className={classes.uploadButton}
          color='secondary'
          variant='outlined'
          onClick={handleUploadFileButtonClick}
        >
          Upload {ARTIFACT_SELECTION_DEFAULTS.get(selectionType)?.extensions[0]}
        </Button>
      </div>
      <DropzoneContainer
        dropzoneRef={dropzoneRef}
        onFileDrop={handleDrop}
        accept={ARTIFACT_SELECTION_DEFAULTS.get(selectionType)?.extensions}
        showDropzone={(children && showDropzone) || (artifacts && !artifactsBusy && showDropzone)}
        disabled={disableUpload}
      >
        <div className={classes.gridContainer}>
          {!showDropzone &&
            (!children && artifacts && !artifactsBusy ? (
              <ArtifactsList
                artifacts={artifacts || []}
                artifactTypeOrder={
                  ARTIFACT_SELECTION_DEFAULTS.get(selectionType)
                    ? [ARTIFACT_SELECTION_DEFAULTS.get(selectionType)?.artifactType || '']
                    : undefined
                }
                density='compact'
                isBusy={artifactsBusy}
                linkConfig
                noRowsMessage={<span>No files</span>}
                selectionModel={selected}
                checkboxSelection
                disableMultipleSelection={singleSelect}
                disableSelectionOnClick={false}
                onSelectionModelChange={singleSelect ? handleSingleSelect : handleMultiSelect}
                isRowSelectable={isRowSelectable}
                components={{
                  Toolbar: ControllerGridQuickFilterToolbar,
                }}
                componentsProps={{
                  toolbar: { density: 'compact' },
                }}
                sx={
                  singleSelect
                    ? {
                        // hide "select all" input when `singleSelect=true`
                        '& .MuiDataGrid-columnHeaderCheckbox .MuiDataGrid-columnHeaderTitleContainer': {
                          display: 'none',
                        },
                      }
                    : undefined
                }
              />
            ) : (
              children
            ))}
        </div>
      </DropzoneContainer>
      <Modal
        disablePortal
        open={!!fileName && !uploadError && uploadBusy}
        sx={{ position: 'absolute' }}
        BackdropProps={{ sx: { position: 'absolute' } }}
      >
        <Box
          width='100%'
          height='100%'
          display='flex'
          justifyContent='center'
          alignItems='center'
          color='white'
        >
          <strong>{`Uploading ${fileName} ${fileSize ? ` (${getDataSize(fileSize)})` : ''}`}</strong>
          <CircularProgress size='1em' color='inherit' sx={{ marginLeft: 1 }} />
        </Box>
      </Modal>
    </section>
  );
}

interface SelectArtifactProps {
  artifacts?: ArtifactModel[];
  artifactsBusy?: boolean;
  selectionType: SelectionType;
  singleSelect?: boolean;
  selected?: GridSelectionModel;
  header?: React.ReactElement;
  showDropzone?: boolean;
  disableUpload?: boolean;
  onSelect?: (selectedArtifacts: ArtifactModel[]) => void;
  isRowSelectable?: (params: GridRowParams) => boolean;
  onLoadConfig?: (config: ConfigModel) => void;
  onUploadSuccess?: (artifact: ArtifactModel) => void;
  onError?: (message: string, reason: ErrorReason) => void;
  children?: React.ReactElement;
}
